import React, { useState } from 'react'
import { useParams, Navigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { 
  CheckCircle,
  Calendar,
  MessageCircle,
  ArrowLeft,
  Search,
  Plus
} from 'lucide-react'
import { useSolutions } from '../hooks/useSolutions'
import { ImageGallery } from '../components/common/ImageGallery'

export const ProductDetailPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>()
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const solutions = useSolutions()
  const { t } = useTranslation(['solutions', 'gallery_translations', 'beauty_translations', 'erp_translations'])
  
  // Redirect workforce to existing page
  if (productId === 'workforce') {
    return <Navigate to="/workforce" replace />
  }
  
  const product = solutions.find(s => s.id === productId)
  
  if (!product) {
    return <Navigate to="/solutions" replace />
  }

  // Fashion/Clothing management gallery images
  const clothingGalleryImages = productId === 'fashion' ? [
    {
      src: '/images/roupas/roupas1.png',
      alt: t('gallery_translations:productDetail.gallery.fashion.elegantInterface.alt'),
      title: t('gallery_translations:productDetail.gallery.fashion.elegantInterface.title'),
      description: t('gallery_translations:productDetail.gallery.fashion.elegantInterface.description')
    },
    {
      src: '/images/roupas/roupas2.png',
      alt: t('gallery_translations:productDetail.gallery.fashion.premiumShowcase.alt'),
      title: t('gallery_translations:productDetail.gallery.fashion.premiumShowcase.title'),
      description: t('gallery_translations:productDetail.gallery.fashion.premiumShowcase.description')
    },
    // Nova imagem de integração com Instagram
    {
      src: '/images/roupas/roupas99.png',
      alt: t('gallery_translations:productDetail.gallery.fashion.instagramIntegration.alt'),
      title: t('gallery_translations:productDetail.gallery.fashion.instagramIntegration.title'),
      description: t('gallery_translations:productDetail.gallery.fashion.instagramIntegration.description')
    },
    {
      src: '/images/roupas/roupas3.png',
      alt: t('gallery_translations:productDetail.gallery.fashion.productDetails.alt'),
      title: t('gallery_translations:productDetail.gallery.fashion.productDetails.title'),
      description: t('gallery_translations:productDetail.gallery.fashion.productDetails.description')
    },
    {
      src: '/images/roupas/roupas4.png',
      alt: t('gallery_translations:productDetail.gallery.fashion.optimizedCheckout.alt'),
      title: t('gallery_translations:productDetail.gallery.fashion.optimizedCheckout.title'),
      description: t('gallery_translations:productDetail.gallery.fashion.optimizedCheckout.description')
    },
    {
      src: '/images/roupas/roupas5.png',
      alt: t('gallery_translations:productDetail.gallery.fashion.managementPanel.alt'),
      title: t('gallery_translations:productDetail.gallery.fashion.managementPanel.title'),
      description: t('gallery_translations:productDetail.gallery.fashion.managementPanel.description')
    },
    {
      src: '/images/roupas/roupas6.png',
      alt: t('gallery_translations:productDetail.gallery.fashion.salesControl.alt'),
      title: t('gallery_translations:productDetail.gallery.fashion.salesControl.title'),
      description: t('gallery_translations:productDetail.gallery.fashion.salesControl.description')
    },
    {
      src: '/images/roupas/roupas7.png',
      alt: t('gallery_translations:productDetail.gallery.fashion.collectionsManagement.alt'),
      title: t('gallery_translations:productDetail.gallery.fashion.collectionsManagement.title'),
      description: t('gallery_translations:productDetail.gallery.fashion.collectionsManagement.description')
    },
    {
      src: '/images/roupas/roupas8.png',
      alt: t('gallery_translations:productDetail.gallery.fashion.productCuration.alt'),
      title: t('gallery_translations:productDetail.gallery.fashion.productCuration.title'),
      description: t('gallery_translations:productDetail.gallery.fashion.productCuration.description')
    },
    {
      src: '/images/roupas/roupas9.png',
      alt: t('gallery_translations:productDetail.gallery.fashion.socialMarketing.alt'),
      title: t('gallery_translations:productDetail.gallery.fashion.socialMarketing.title'),
      description: t('gallery_translations:productDetail.gallery.fashion.socialMarketing.description')
    },
    {
      src: '/images/roupas/roupas_mobile.png',
      alt: t('gallery_translations:productDetail.gallery.fashion.mobileResponsive.alt'),
      title: t('gallery_translations:productDetail.gallery.fashion.mobileResponsive.title'),
      description: t('gallery_translations:productDetail.gallery.fashion.mobileResponsive.description')
    }
  ] : []

  // Pet Shop management gallery images
  const petshopGalleryImages = productId === 'petshop' ? [
    {
      src: '/images/pet/pet1.png',
      alt: t('gallery_translations:productDetail.gallery.petshop.mainInterface.alt'),
      title: t('gallery_translations:productDetail.gallery.petshop.mainInterface.title'),
      description: t('gallery_translations:productDetail.gallery.petshop.mainInterface.description')
    },
    {
      src: '/images/pet/pet2.png',
      alt: t('gallery_translations:productDetail.gallery.petshop.transformationsGallery.alt'),
      title: t('gallery_translations:productDetail.gallery.petshop.transformationsGallery.title'),
      description: t('gallery_translations:productDetail.gallery.petshop.transformationsGallery.description')
    },
    {
      src: '/images/pet/pet3.png',
      alt: t('gallery_translations:productDetail.gallery.petshop.premiumCatalog.alt'),
      title: t('gallery_translations:productDetail.gallery.petshop.premiumCatalog.title'),
      description: t('gallery_translations:productDetail.gallery.petshop.premiumCatalog.description')
    },
    {
      src: '/images/pet/pet4.png',
      alt: t('gallery_translations:productDetail.gallery.petshop.expandedCatalog.alt'),
      title: t('gallery_translations:productDetail.gallery.petshop.expandedCatalog.title'),
      description: t('gallery_translations:productDetail.gallery.petshop.expandedCatalog.description')
    },
    {
      src: '/images/pet/pet5.png',
      alt: t('gallery_translations:productDetail.gallery.petshop.productDetails.alt'),
      title: t('gallery_translations:productDetail.gallery.petshop.productDetails.title'),
      description: t('gallery_translations:productDetail.gallery.petshop.productDetails.description')
    },
    {
      src: '/images/pet/pet6.png',
      alt: t('gallery_translations:productDetail.gallery.petshop.adminDashboard.alt'),
      title: t('gallery_translations:productDetail.gallery.petshop.adminDashboard.title'),
      description: t('gallery_translations:productDetail.gallery.petshop.adminDashboard.description')
    },
    {
      src: '/images/pet/pet7.png',
      alt: t('gallery_translations:productDetail.gallery.petshop.scheduling.alt'),
      title: t('gallery_translations:productDetail.gallery.petshop.scheduling.title'),
      description: t('gallery_translations:productDetail.gallery.petshop.scheduling.description')
    },
    {
      src: '/images/pet/pet8.png',
      alt: t('gallery_translations:productDetail.gallery.petshop.galleryManagement.alt'),
      title: t('gallery_translations:productDetail.gallery.petshop.galleryManagement.title'),
      description: t('gallery_translations:productDetail.gallery.petshop.galleryManagement.description')
    },
    {
      src: '/images/pet/pet9.png',
      alt: t('gallery_translations:productDetail.gallery.petshop.knowledgeBase.alt'),
      title: t('gallery_translations:productDetail.gallery.petshop.knowledgeBase.title'),
      description: t('gallery_translations:productDetail.gallery.petshop.knowledgeBase.description')
    },
    {
      src: '/images/pet/pet10.png',
      alt: t('gallery_translations:productDetail.gallery.petshop.teamManagement.alt'),
      title: t('gallery_translations:productDetail.gallery.petshop.teamManagement.title'),
      description: t('gallery_translations:productDetail.gallery.petshop.teamManagement.description')
    },
    {
      src: '/images/pet/pet11.png',
      alt: t('gallery_translations:productDetail.gallery.petshop.clientDashboard.alt'),
      title: t('gallery_translations:productDetail.gallery.petshop.clientDashboard.title'),
      description: t('gallery_translations:productDetail.gallery.petshop.clientDashboard.description')
    },
    {
      src: '/images/pet/pet12.png',
      alt: t('gallery_translations:productDetail.gallery.petshop.chat.alt'),
      title: t('gallery_translations:productDetail.gallery.petshop.chat.title'),
      description: t('gallery_translations:productDetail.gallery.petshop.chat.description')
    },
    {
      src: '/images/pet/pet13.png',
      alt: t('gallery_translations:productDetail.gallery.petshop.assistedScheduling.alt'),
      title: t('gallery_translations:productDetail.gallery.petshop.assistedScheduling.title'),
      description: t('gallery_translations:productDetail.gallery.petshop.assistedScheduling.description')
    },
    {
      src: '/images/pet/pet14.png',
      alt: t('gallery_translations:productDetail.gallery.petshop.scheduleConfirmation.alt'),
      title: t('gallery_translations:productDetail.gallery.petshop.scheduleConfirmation.title'),
      description: t('gallery_translations:productDetail.gallery.petshop.scheduleConfirmation.description')
    },
    {
      src: '/images/pet/pet_mobile.png',
      alt: t('gallery_translations:productDetail.gallery.petshop.mobileExperience.alt'),
      title: t('gallery_translations:productDetail.gallery.petshop.mobileExperience.title'),
      description: t('gallery_translations:productDetail.gallery.petshop.mobileExperience.description')
    }
  ] : []

  // Beauty management gallery images
  const beautyGalleryImages = productId === 'beauty' ? [
    {
      src: '/images/beauty/Screenshot From 2025-09-03 17-16-19.png',
      alt: t('beauty_translations:productDetail.gallery.beauty.transformations.alt'),
      title: t('beauty_translations:productDetail.gallery.beauty.transformations.title'),
      description: t('beauty_translations:productDetail.gallery.beauty.transformations.description')
    },
    {
      src: '/images/beauty/Screenshot From 2025-09-03 17-29-14.png',
      alt: t('beauty_translations:productDetail.gallery.beauty.premiumProducts.alt'),
      title: t('beauty_translations:productDetail.gallery.beauty.premiumProducts.title'),
      description: t('beauty_translations:productDetail.gallery.beauty.premiumProducts.description')
    },
    {
      src: '/images/beauty/Screenshot From 2025-09-03 17-29-30.png',
      alt: t('beauty_translations:productDetail.gallery.beauty.specializedTeam.alt'),
      title: t('beauty_translations:productDetail.gallery.beauty.specializedTeam.title'),
      description: t('beauty_translations:productDetail.gallery.beauty.specializedTeam.description')
    },
    {
      src: '/images/beauty/Screenshot From 2025-09-03 17-29-56.png',
      alt: t('beauty_translations:productDetail.gallery.beauty.intelligentScheduling.alt'),
      title: t('beauty_translations:productDetail.gallery.beauty.intelligentScheduling.title'),
      description: t('beauty_translations:productDetail.gallery.beauty.intelligentScheduling.description')
    },
    {
      src: '/images/beauty/Screenshot From 2025-09-03 17-30-05.png',
      alt: t('beauty_translations:productDetail.gallery.beauty.clientArea.alt'),
      title: t('beauty_translations:productDetail.gallery.beauty.clientArea.title'),
      description: t('beauty_translations:productDetail.gallery.beauty.clientArea.description')
    },
    {
      src: '/images/beauty/Screenshot From 2025-09-03 17-30-11.png',
      alt: t('beauty_translations:productDetail.gallery.beauty.adminPanel.alt'),
      title: t('beauty_translations:productDetail.gallery.beauty.adminPanel.title'),
      description: t('beauty_translations:productDetail.gallery.beauty.adminPanel.description')
    },
    {
      src: '/images/beauty/Screenshot From 2025-09-03 17-30-28.png',
      alt: t('beauty_translations:productDetail.gallery.beauty.serviceManagement.alt'),
      title: t('beauty_translations:productDetail.gallery.beauty.serviceManagement.title'),
      description: t('beauty_translations:productDetail.gallery.beauty.serviceManagement.description')
    },
    {
      src: '/images/beauty/Screenshot From 2025-09-03 17-33-25.png',
      alt: t('beauty_translations:productDetail.gallery.beauty.mobileApp.alt'),
      title: t('beauty_translations:productDetail.gallery.beauty.mobileApp.title'),
      description: t('beauty_translations:productDetail.gallery.beauty.mobileApp.description')
    },
    {
      src: '/images/beauty/Screenshot From 2025-09-03 17-34-47.png',
      alt: t('beauty_translations:productDetail.gallery.beauty.webInterface.alt'),
      title: t('beauty_translations:productDetail.gallery.beauty.webInterface.title'),
      description: t('beauty_translations:productDetail.gallery.beauty.webInterface.description')
    },
    {
      src: '/images/beauty/Screenshot From 2025-09-03 17-35-39.png',
      alt: t('beauty_translations:productDetail.gallery.beauty.notifications.alt'),
      title: t('beauty_translations:productDetail.gallery.beauty.notifications.title'),
      description: t('beauty_translations:productDetail.gallery.beauty.notifications.description')
    },
    {
      src: '/images/beauty/Screenshot From 2025-09-03 17-35-57.png',
      alt: t('beauty_translations:productDetail.gallery.beauty.reports.alt'),
      title: t('beauty_translations:productDetail.gallery.beauty.reports.title'),
      description: t('beauty_translations:productDetail.gallery.beauty.reports.description')
    },
    {
      src: '/images/beauty/Screenshot From 2025-09-03 17-37-48.png',
      alt: t('beauty_translations:productDetail.gallery.beauty.ecommerce.alt'),
      title: t('beauty_translations:productDetail.gallery.beauty.ecommerce.title'),
      description: t('beauty_translations:productDetail.gallery.beauty.ecommerce.description')
    },
    {
      src: '/images/beauty/Screenshot From 2025-09-03 17-39-19.png',
      alt: t('beauty_translations:productDetail.gallery.beauty.financial.alt'),
      title: t('beauty_translations:productDetail.gallery.beauty.financial.title'),
      description: t('beauty_translations:productDetail.gallery.beauty.financial.description')
    },
    {
      src: '/images/beauty/Screenshot From 2025-09-03 17-50-45.png',
      alt: t('beauty_translations:productDetail.gallery.beauty.dashboard.alt'),
      title: t('beauty_translations:productDetail.gallery.beauty.dashboard.title'),
      description: t('beauty_translations:productDetail.gallery.beauty.dashboard.description')
    },
    {
      src: '/images/beauty/Screenshot From 2025-09-03 17-50-52.png',
      alt: t('beauty_translations:productDetail.gallery.beauty.settings.alt'),
      title: t('beauty_translations:productDetail.gallery.beauty.settings.title'),
      description: t('beauty_translations:productDetail.gallery.beauty.settings.description')
    }
  ] : []

  // ERP management gallery images
  const erpGalleryImages = productId === 'erp' ? [
    {
      src: '/images/erp/0.mobile.png',
      alt: t('erp_translations:productDetail.gallery.erp.mobileInterface.alt'),
      title: t('erp_translations:productDetail.gallery.erp.mobileInterface.title'),
      description: t('erp_translations:productDetail.gallery.erp.mobileInterface.description')
    },
    {
      src: '/images/erp/1.principal.png',
      alt: t('erp_translations:productDetail.gallery.erp.mainDashboard.alt'),
      title: t('erp_translations:productDetail.gallery.erp.mainDashboard.title'),
      description: t('erp_translations:productDetail.gallery.erp.mainDashboard.description')
    },
    {
      src: '/images/erp/2.tela_vendas.png',
      alt: t('erp_translations:productDetail.gallery.erp.advancedSales.alt'),
      title: t('erp_translations:productDetail.gallery.erp.advancedSales.title'),
      description: t('erp_translations:productDetail.gallery.erp.advancedSales.description')
    },
    {
      src: '/images/erp/3.produtos.png',
      alt: t('erp_translations:productDetail.gallery.erp.productCatalog.alt'),
      title: t('erp_translations:productDetail.gallery.erp.productCatalog.title'),
      description: t('erp_translations:productDetail.gallery.erp.productCatalog.description')
    },
    {
      src: '/images/erp/4.produto_midia.png',
      alt: t('erp_translations:productDetail.gallery.erp.mediaManagement.alt'),
      title: t('erp_translations:productDetail.gallery.erp.mediaManagement.title'),
      description: t('erp_translations:productDetail.gallery.erp.mediaManagement.description')
    },
    {
      src: '/images/erp/5.produto_sku.png',
      alt: t('erp_translations:productDetail.gallery.erp.skuControl.alt'),
      title: t('erp_translations:productDetail.gallery.erp.skuControl.title'),
      description: t('erp_translations:productDetail.gallery.erp.skuControl.description')
    },
    {
      src: '/images/erp/6.nfe_import.png',
      alt: t('erp_translations:productDetail.gallery.erp.nfeImport.alt'),
      title: t('erp_translations:productDetail.gallery.erp.nfeImport.title'),
      description: t('erp_translations:productDetail.gallery.erp.nfeImport.description')
    },
    {
      src: '/images/erp/7.cadastro_ia.png',
      alt: t('erp_translations:productDetail.gallery.erp.aiRegistration.alt'),
      title: t('erp_translations:productDetail.gallery.erp.aiRegistration.title'),
      description: t('erp_translations:productDetail.gallery.erp.aiRegistration.description')
    },
    {
      src: '/images/erp/8.inventory1.png',
      alt: t('erp_translations:productDetail.gallery.erp.inventoryOverview.alt'),
      title: t('erp_translations:productDetail.gallery.erp.inventoryOverview.title'),
      description: t('erp_translations:productDetail.gallery.erp.inventoryOverview.description')
    },
    {
      src: '/images/erp/9.inventory2.png',
      alt: t('erp_translations:productDetail.gallery.erp.detailedInventory.alt'),
      title: t('erp_translations:productDetail.gallery.erp.detailedInventory.title'),
      description: t('erp_translations:productDetail.gallery.erp.detailedInventory.description')
    },
    {
      src: '/images/erp/91.mobile_inventario.png',
      alt: t('erp_translations:productDetail.gallery.erp.mobileInventory.alt'),
      title: t('erp_translations:productDetail.gallery.erp.mobileInventory.title'),
      description: t('erp_translations:productDetail.gallery.erp.mobileInventory.description')
    },
    {
      src: '/images/erp/92.comprovante_nao_fiscal.png',
      alt: t('erp_translations:productDetail.gallery.erp.nonFiscalReceipts.alt'),
      title: t('erp_translations:productDetail.gallery.erp.nonFiscalReceipts.title'),
      description: t('erp_translations:productDetail.gallery.erp.nonFiscalReceipts.description')
    },
    {
      src: '/images/erp/93.danfe.png',
      alt: t('erp_translations:productDetail.gallery.erp.danfeGeneration.alt'),
      title: t('erp_translations:productDetail.gallery.erp.danfeGeneration.title'),
      description: t('erp_translations:productDetail.gallery.erp.danfeGeneration.description')
    },
    {
      src: '/images/erp/94.dashboard.png',
      alt: t('erp_translations:productDetail.gallery.erp.executiveDashboard.alt'),
      title: t('erp_translations:productDetail.gallery.erp.executiveDashboard.title'),
      description: t('erp_translations:productDetail.gallery.erp.executiveDashboard.description')
    },
    {
      src: '/images/erp/95.filtros_avancados.png',
      alt: t('erp_translations:productDetail.gallery.erp.advancedFilters.alt'),
      title: t('erp_translations:productDetail.gallery.erp.advancedFilters.title'),
      description: t('erp_translations:productDetail.gallery.erp.advancedFilters.description')
    },
    {
      src: '/images/erp/96.nfe_config.png',
      alt: t('erp_translations:productDetail.gallery.erp.nfeConfiguration.alt'),
      title: t('erp_translations:productDetail.gallery.erp.nfeConfiguration.title'),
      description: t('erp_translations:productDetail.gallery.erp.nfeConfiguration.description')
    },
    {
      src: '/images/erp/97.relatorios.png',
      alt: t('erp_translations:productDetail.gallery.erp.managementReports.alt'),
      title: t('erp_translations:productDetail.gallery.erp.managementReports.title'),
      description: t('erp_translations:productDetail.gallery.erp.managementReports.description')
    },
    {
      src: '/images/erp/98.programas.png',
      alt: t('erp_translations:productDetail.gallery.erp.modulesPrograms.alt'),
      title: t('erp_translations:productDetail.gallery.erp.modulesPrograms.title'),
      description: t('erp_translations:productDetail.gallery.erp.modulesPrograms.description')
    }
  ] : []

  // Dynamic Forms gallery images
  const formstudioGalleryImages = productId === 'forms' ? [
    {
      src: '/images/formstudio/1-main.png',
      alt: 'Interface Principal do Dynamic Forms',
      title: 'Dynamic Form Builder Interface',
      description: 'Interface principal do sistema Dynamic Forms mostrando a capacidade de criação e gestão de formulários dinâmicos em tempo real, sem necessidade de reinicialização do servidor.'
    },
    {
      src: '/images/formstudio/DragDrop.png',
      alt: 'Drag and Drop Form Builder',
      title: 'Drag and Drop Interface',
      description: 'Interface intuitiva de arrastar e soltar para criar formulários complexos sem código. Sistema visual que permite organizar campos, configurar validações e criar layouts responsivos de forma simples e rápida.'
    },
    {
      src: '/images/formstudio/EditorJson.png',
      alt: 'Editor JSON Avançado',
      title: 'JSON Schema Editor',
      description: 'Editor JSON avançado para usuários técnicos que precisam de controle total sobre a estrutura dos formulários. Validação em tempo real e preview instantâneo das alterações.'
    },
    {
      src: '/images/formstudio/FormBuilder.png',
      alt: 'Construtor de Formulários',
      title: 'Advanced Form Builder',
      description: 'Ferramenta completa de construção de formulários com biblioteca de componentes pré-construídos, validações customizadas e regras de negócio configuráveis.'
    },
    {
      src: '/images/formstudio/NewTab.png',
      alt: 'Sistema de Abas',
      title: 'Multi-Tab Management',
      description: 'Sistema de gerenciamento de múltiplas abas para trabalhar com vários formulários simultaneamente, aumentando a produtividade e permitindo comparações entre diferentes estruturas.'
    },
    {
      src: '/images/formstudio/Program.png',
      alt: 'Gestão de Programas',
      title: 'Program Management',
      description: 'Resultado do programa gerado pelo FormStudio, exibindo a estrutura do formulário com todos os campos, validações e regras de negócio configuradas.'
    },
    {
      src: '/images/formstudio/TableConfig.png',
      alt: 'Configuração de Tabelas',
      title: 'Dynamic Table Configuration',
      description: 'Configurador avançado de tabelas dinâmicas com suporte a diferentes tipos de dados, filtros personalizados e exportação em múltiplos formatos.'
    }
  ] : []

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className={`py-24 ${product.bgColor}`}>
        <div className="container">
          <div className="max-w-5xl mx-auto">
            {/* Back Navigation */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="mb-8"
            >
              <Link 
                to="/solutions" 
                className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('productDetail.navigation.backToSolutions')}
              </Link>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className={`inline-flex items-center gap-3 px-4 py-2 bg-white rounded-full text-sm font-semibold mb-6`}>
                  <product.icon className={`w-5 h-5 ${product.color}`} />
                  {product.title}
                </div>
                
                <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight text-gray-900">
                  {product.subtitle}
                </h1>
                
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  {product.longDescription}
                </p>

                <div className="flex flex-wrap gap-4">
                  <Link 
                    to="/contact"
                    className={`${productId === 'beauty' ? 'text-white font-bold py-4 px-8 rounded-lg hover:opacity-90 transition-colors inline-flex items-center gap-2' : 'btn-primary'}`}
                    style={productId === 'beauty' ? { backgroundColor: '#ab8755' } : {}}
                  >
                    <Calendar className="w-5 h-5" />
                    {t('productDetail.hero.scheduleDemo')}
                  </Link>
                  <button className="btn-secondary">
                    <MessageCircle className="w-5 h-5" />
                    {t('productDetail.hero.talkToSpecialist')}
                  </button>
                </div>
              </motion.div>

              {/* Right Content - Visual Showcase */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                {product.image ? (
                  <div className="relative">
                    {/* Main Desktop Image */}
                    <div 
                      className={`bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden transform rotate-2 hover:rotate-0 transition-transform duration-500 ${(productId === 'beauty' || productId === 'fashion' || productId === 'petshop' || productId === 'erp' || productId === 'forms') ? 'cursor-pointer' : ''}`}
                      onClick={() => {
                        if (productId === 'beauty' || productId === 'fashion' || productId === 'petshop' || productId === 'erp' || productId === 'forms') {
                          setIsGalleryOpen(true)
                        }
                      }}
                    >
                      <img
                        src={product.image}
                        alt={product.title}
                        className="w-full h-auto"
                      />
                      {(productId === 'beauty' || productId === 'fashion' || productId === 'petshop' || productId === 'erp' || productId === 'forms') && (
                        <div className="absolute top-4 right-4 bg-white bg-opacity-90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                          <div className="relative">
                            <Search className="w-5 h-5 text-gray-700" />
                            <Plus className="w-3 h-3 text-gray-700 absolute -top-1 -right-1" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Mobile App Screenshot */}
                    {productId === 'beauty' && (
                      <div className="absolute -bottom-8 -right-8 bg-white rounded-xl shadow-xl p-2 transform -rotate-12 hover:rotate-0 transition-transform duration-500 z-10">
                        <img 
                          src="/images/beauty/Screenshot From 2025-09-03 17-50-52.png" 
                          alt="Beauty Mobile App"
                          className="w-32 h-auto rounded-lg"
                        />
                        <div className="text-xs text-center mt-2 font-medium text-gray-700">{t('productDetail.gallery.mobileApp')}</div>
                      </div>
                    )}

                    {/* Fashion Mobile App Screenshot */}
                    {productId === 'fashion' && (
                      <div className="absolute -bottom-8 -right-8 bg-white rounded-xl shadow-xl p-2 transform -rotate-12 hover:rotate-0 transition-transform duration-500 z-10">
                        <img 
                          src="/images/roupas/roupas_mobile.png" 
                          alt="Fashion Mobile App"
                          className="w-32 h-auto rounded-lg"
                        />
                        <div className="text-xs text-center mt-2 font-medium text-gray-700">{t('productDetail.gallery.mobileApp')}</div>
                      </div>
                    )}

                    {/* Pet Shop Mobile App Screenshot */}
                    {productId === 'petshop' && (
                      <div className="absolute -bottom-8 -right-8 bg-white rounded-xl shadow-xl p-2 transform -rotate-12 hover:rotate-0 transition-transform duration-500 z-10">
                        <img 
                          src="/images/pet/pet_mobile.png" 
                          alt="Pet Shop Mobile App"
                          className="w-32 h-auto rounded-lg"
                        />
                        <div className="text-xs text-center mt-2 font-medium text-gray-700">{t('productDetail.gallery.mobileApp')}</div>
                      </div>
                    )}

                    {/* ERP Mobile App Screenshot */}
                    {productId === 'erp' && (
                      <div className="absolute -bottom-8 -right-8 bg-white rounded-xl shadow-xl p-2 transform -rotate-12 hover:rotate-0 transition-transform duration-500 z-10">
                        <img 
                          src="/images/erp/0.mobile.png" 
                          alt="ERP Mobile App"
                          className="w-32 h-auto rounded-lg"
                        />
                        <div className="text-xs text-center mt-2 font-medium text-gray-700">{t('productDetail.gallery.mobileApp')}</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl shadow-xl aspect-video flex items-center justify-center">
                    <div className={`w-32 h-32 ${product.bgColor} rounded-full flex items-center justify-center`}>
                      <product.icon className={`w-16 h-16 ${product.color}`} />
                    </div>
                  </div>
                )}

                {/* Gallery Button Below Image - Beauty, Fashion, Pet Shop, ERP and Dynamic Forms */}
                {(productId === 'beauty' || productId === 'fashion' || productId === 'petshop' || productId === 'erp' || productId === 'forms') && (
                  <div className="mt-4 flex justify-center">
                    <button 
                      onClick={() => setIsGalleryOpen(true)}
                      className="font-medium flex items-center gap-2 transition-colors"
                      style={{ 
                        color: productId === 'beauty' ? '#ab8755' : 
                               productId === 'fashion' ? '#8b7355' : 
                               productId === 'petshop' ? '#e07f3b' : 
                               productId === 'erp' ? '#2563eb' : 
                               productId === 'forms' ? '#4f46e5' : '#8b7355'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = 
                        productId === 'beauty' ? '#8b6d3f' : 
                        productId === 'fashion' ? '#6d5a43' : 
                        productId === 'petshop' ? '#c5672e' : 
                        productId === 'erp' ? '#1d4ed8' : 
                        productId === 'forms' ? '#3730a3' : '#6d5a43'
                      }
                      onMouseLeave={(e) => e.currentTarget.style.color = 
                        productId === 'beauty' ? '#ab8755' : 
                        productId === 'fashion' ? '#8b7355' : 
                        productId === 'petshop' ? '#e07f3b' : 
                        productId === 'erp' ? '#2563eb' : 
                        productId === 'forms' ? '#4f46e5' : '#8b7355'
                      }
                    >
                      <Search className="w-4 h-4" />
                      {t('productDetail.gallery.clickToView')}
                    </button>
                  </div>
                )}
              </motion.div>

            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl font-bold text-gray-900 mb-4"
              >
                {t('productDetail.features.title')}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-lg text-gray-600 max-w-2xl mx-auto"
              >
                {t('productDetail.features.description', { subtitle: product.subtitle.toLowerCase() })}
              </motion.p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {product.features.map((feature, index) => (
                <motion.div
                  key={feature}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <CheckCircle className={`w-6 h-6 ${product.color} flex-shrink-0`} />
                  <span className="text-gray-700 font-medium">{feature}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-gray-50">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-3xl font-bold text-gray-900 mb-4"
              >
                {t('productDetail.benefits.title')}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-lg text-gray-600 max-w-2xl mx-auto"
              >
                {t('productDetail.benefits.description', { title: product.title })}
              </motion.p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {product.benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className={`w-16 h-16 ${product.bgColor} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                    <product.icon className={`w-8 h-8 ${product.color}`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-24 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl font-bold text-gray-900 mb-4"
            >
              {t('productDetail.technologies.title')}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-lg text-gray-600 mb-12"
            >
              {t('productDetail.technologies.description')}
            </motion.p>

            <div className="flex flex-wrap gap-3 justify-center">
              {product.technologies.map((tech, index) => (
                <motion.span
                  key={tech}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  {tech}
                </motion.span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className={`py-24 text-white ${productId !== 'beauty' ? 'bg-gradient-to-r from-gray-900 to-gray-800' : ''}`}
        style={productId === 'beauty' ? { background: '#ab8755' } : {}}
      >
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl font-bold mb-6"
            >
              {t('productDetail.finalCta.title', { title: product.title })}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-xl mb-8 opacity-90"
            >
              {t('productDetail.finalCta.description')}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex flex-wrap gap-4 justify-center"
            >
              <Link 
                to="/contact"
                className={`px-8 py-4 font-bold rounded-lg transition-colors inline-flex items-center gap-2 ${
                  productId === 'beauty' 
                    ? 'text-white border-2 border-white hover:text-gray-900' 
                    : 'bg-white text-gray-900 hover:bg-gray-100'
                }`}
                style={productId === 'beauty' ? { 
                  backgroundColor: 'transparent',
                  borderColor: 'white',
                  color: 'white'
                } : {}}
                onMouseEnter={(e) => {
                  if (productId === 'beauty') {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.color = '#ab8755';
                  }
                }}
                onMouseLeave={(e) => {
                  if (productId === 'beauty') {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'white';
                  }
                }}
              >
                <Calendar className="w-5 h-5" />
                {t('productDetail.finalCta.buttons.freeDemo')}
              </Link>
              <button className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-gray-900 transition-colors inline-flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                {t('productDetail.finalCta.buttons.commercial')}
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      {productId === 'beauty' && (
        <ImageGallery
          images={beautyGalleryImages}
          isOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
        />
      )}

      {/* Fashion Image Gallery */}
      {productId === 'fashion' && (
        <ImageGallery
          images={clothingGalleryImages}
          isOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
        />
      )}

      {/* Pet Shop Image Gallery */}
      {productId === 'petshop' && (
        <ImageGallery
          images={petshopGalleryImages}
          isOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
        />
      )}

      {/* ERP Image Gallery */}
      {productId === 'erp' && (
        <ImageGallery
          images={erpGalleryImages}
          isOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
        />
      )}

      {/* Forms Image Gallery */}
      {productId === 'forms' && (
        <ImageGallery
          images={formstudioGalleryImages}
          isOpen={isGalleryOpen}
          onClose={() => setIsGalleryOpen(false)}
        />
      )}
    </div>
  )
}